// ─────────────────────────────────────────────────────────────────────────────
// WHO ARE YOU, BEFORE THE FORM ASKS ANYTHING ELSE.
//
// Two questions ahead of /doorlypro/register. Q1 is the partner type; Q2 is
// whether they are an individual or the firm — and Q2 is the DOOR: an individual
// registers as a PARTNER, a broker or team lead registers as an ACCOUNT.
//
// ⚠️ WRITTEN IN PreQualification's SHAPE, NOT EXTRACTED FROM IT. Same FLOW array,
// same screen state machine, same slide transition and progress chrome — but its
// own component. That page is 945 lines of purchase price, state eligibility,
// rejection branches, confetti and three modals; a generic wizard pulled out of
// it would be mostly machinery this flow never uses, and the extraction would be
// a refactor of a working borrower funnel to serve two questions.
//
// ── 🚨 WHAT THESE ANSWERS ARE, AND WHAT THEY ARE NOT ─────────────────────────
//
// THEY ARE A ROUTER, NOT AN AUTHORIZATION, AND THE DOOR IS NOT CLOSED HERE.
// Everything this component collects is self-asserted and editable — by the
// param, by devtools, by typing a different URL. That is not a regression: today
// the register form carries a Partner Type <select> the partner sets freely, so
// the assertion is the same one in a different control.
//
// 🚨 BUT THE ENTERPRISE DOOR IS CONSEQUENTIAL AND MUST BE CONFIRMED DOWNSTREAM.
// Creating an account is not merely a different form. The first partner to save
// banking on an account becomes its payee contact — routes/doorlypro.js writes
// `payee_contact_partner_id = COALESCE(payee_contact_partner_id, ?)`, so it is
// first-writer-wins and sticky. An agent who claims `broker` here, creates an
// account named after their real firm and enters their own banking becomes that
// firm's payee, and colleagues are then proposed to join it. The claim is made
// here; it is CONFIRMED where the evidence is:
//
//   realtor + broker  → TREC settles it. verifyRealtorLicense() needs no partner
//                       row, so the license type can be checked BEFORE the
//                       account exists: 800899 answers "Sales Agent" and refuses
//                       the claim outright.
//   originator+broker → nothing can settle it. There is no NMLS lookup, so the
//                       account is created and payee authority is withheld
//                       rather than conferred by the banking form.
//
// Neither of those lives in this file. What this file must not do is imply the
// question was settled by asking it.
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const FLOW = ['type', 'role'];

// ⚠️ Q2 IS KEYED OFF Q1 RATHER THAN BEING THREE SCREENS. The question differs by
// type but the shape does not, and three near-identical screens would drift the
// first time one of them was edited.
const ROLE_QUESTION = {
  realtor: {
    heading: 'Are you an agent, or the broker for your firm?',
    options: [
      { value: 'agent', label: 'I’m an agent', sublabel: 'I work under a sponsoring broker' },
      { value: 'broker', label: 'I’m the broker for my firm', sublabel: 'I’ll be setting my firm up on Doorly' },
    ],
  },
  loan_officer: {
    heading: 'Are you an LO or a broker?',
    options: [
      { value: 'lo', label: 'I’m a loan officer', sublabel: 'I originate under my company’s license' },
      { value: 'broker', label: 'I’m a broker', sublabel: 'I’ll be setting my company up on Doorly' },
    ],
  },
  homebuilder: {
    heading: 'Are you a sales agent or a team lead?',
    options: [
      { value: 'agent', label: 'I’m a sales agent', sublabel: 'I sell in one or more communities' },
      { value: 'lead', label: 'I’m a team lead', sublabel: 'I’ll be setting my builder up on Doorly' },
    ],
  },
};

// The door. Everything not on this list registers as an individual partner.
const ENTERPRISE_ROLES = ['broker', 'lead'];

const TYPE_OPTIONS = [
  { value: 'realtor', label: 'Realtor', sublabel: 'I represent buyers on the real estate side' },
  { value: 'loan_officer', label: 'Mortgage Originator', sublabel: 'I originate loans for borrowers' },
  { value: 'homebuilder', label: 'Homebuilder', sublabel: 'I sell homes I or my company build' },
];

function DoorlyProStart() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const inviteId = params.get('invite');

  const [screen, setScreen] = useState(inviteId ? 'loading' : 'type');
  const [animating, setAnimating] = useState(false);
  const [direction, setDirection] = useState('forward');
  const [answers, setAnswers] = useState({});

  // ── an invite answers what it can, and only what it can ──────────────────
  //
  // 🔑 EVERY ENTRY POINT LANDS HERE, INCLUDING THE INVITE. The invite email used
  // to go straight to /register so an invited partner was not re-asked; that made
  // it the one door skipping the question set. Now the questions skip themselves.
  //
  // 🚨 THE INVITE ANSWERS Q1 AND NEVER Q2. partner_invites carries partner_type,
  // so "what partner type are you" is already known. It does NOT record whether
  // the person is an agent or the broker — and deliberately does not infer it
  // from which of the two emails a rep chose, because that answer selects the
  // enterprise registration door and the first partner to save banking on a new
  // account becomes its payee contact permanently. A rep's template choice must
  // not confer that. So an invited partner sees ONE question instead of two.
  //
  // ⚠️ AND A FAILED LOOKUP ASKS BOTH, rather than erroring. A cancelled invite, a
  // stale link or an unreachable server all land on the ordinary flow — the worst
  // outcome is a question somebody answers twice, which is recoverable, unlike a
  // dead end on a registration link.
  useEffect(() => {
    if (!inviteId) return;
    let live = true;
    axios.get(`${API_URL}/api/doorlypro/invite-answers`, { params: { invite: inviteId } })
      .then((r) => {
        if (!live) return;
        const t = r.data && r.data.answers && r.data.answers.partner_type;
        if (t && ROLE_QUESTION[t]) { setAnswers({ partner_type: t }); setScreen('role'); }
        else setScreen('type');
      })
      .catch(() => { if (live) setScreen('type'); });
    return () => { live = false; };
  }, [inviteId]);

  const qIndex = FLOW.indexOf(screen);
  // ⚠️ AN INVITED PARTNER STARTS PART-WAY ALONG, and the bar should say so rather
  // than resetting to 50% as though they had answered nothing.
  const progressPct = qIndex >= 0 ? Math.round(((qIndex + 1) / FLOW.length) * 100) : 0;

  const goTo = (next, dir = 'forward') => {
    setDirection(dir);
    setAnimating(true);
    setTimeout(() => {
      setScreen(next);
      setAnimating(false);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 200);
  };

  const finish = (partnerType, role) => {
    const isEnterprise = ENTERPRISE_ROLES.includes(role);
    const payload = { partner_type: partnerType, role, enterprise: isEnterprise };

    // ⚠️ THREE TRANSPORTS, DELIBERATELY, AND NONE OF THEM IS TRUSTED.
    //   state   — survives the navigation, invisible in the URL
    //   session — survives a REFRESH, which router state does not
    //   params  — survives a copied link, and is what a partner would share
    // The form reads state → session → params and renders a neutral fallback if
    // all three are missing, so a direct visit to /doorlypro/register still
    // works rather than breaking on an absent answer.
    try {
      sessionStorage.setItem('doorlypro_start', JSON.stringify(payload));
    } catch (_) { /* private mode — state and params still carry it */ }

    // ⚠️ THE INVITE ID TRAVELS ON. /register still matches the invite by EMAIL, so
    // attribution does not depend on it — but the redirect guard on /register
    // reads it to know an arrival came from an invite rather than a bookmark.
    const qs = new URLSearchParams({
      type: partnerType, role, ...(isEnterprise ? { enterprise: '1' } : {}),
      ...(inviteId ? { invite: inviteId } : {}),
    }).toString();
    // 🔑 THE ANSWERS CHOOSE THE DESTINATION, not a flag the form reads. An
    // individual goes to partner registration; a broker or team lead goes to
    // account registration, which creates the firm and makes them its broker.
    const dest = isEnterprise ? '/doorlypro/register-account' : '/doorlypro/register';
    navigate(`${dest}?${qs}`, { state: { doorlyproStart: payload } });
  };

  const Choice = ({ label, sublabel, onClick }) => (
    <button
      type="button"
      onClick={onClick}
      className="w-full text-left px-5 py-4 rounded-xl border-2 border-gray-200
      hover:border-doorly-blue hover:bg-blue-50 transition-all duration-150 group"
    >
      <span className="font-medium text-black group-hover:text-doorly-blue transition-colors">
        {label}
      </span>
      {sublabel && <span className="block text-sm text-gray-400 mt-0.5">{sublabel}</span>}
    </button>
  );

  const transition = animating
    ? direction === 'forward' ? 'opacity-0 translate-x-8' : 'opacity-0 -translate-x-8'
    : 'opacity-100 translate-x-0';

  const roleQ = ROLE_QUESTION[answers.partner_type] || ROLE_QUESTION.realtor;

  return (
    <div className="min-h-screen bg-white">
      <header className="sticky top-0 z-20 bg-white/90 backdrop-blur border-b border-gray-100">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 flex items-center justify-between h-16">
          <Link to="/doorlypro" className="flex items-center gap-2 group">
            <img src={`${process.env.PUBLIC_URL}/doorly_logo_black_icon.png`} alt="Doorly" style={{ height: '32px', width: 'auto' }} />
          </Link>
          <div className="flex items-center gap-3 flex-1 mx-6">
            <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
              <div className="h-full bg-doorly-blue rounded-full transition-all duration-500" style={{ width: `${progressPct}%` }} />
            </div>
            <span className="text-xs text-gray-400 w-8 text-right">{progressPct}%</span>
          </div>
          <button
            type="button"
            onClick={() => navigate('/doorlypro')}
            aria-label="Exit partner sign-up"
            className="ml-3 flex items-center justify-center w-9 h-9 text-gray-400 hover:text-black transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </header>

      <main className="max-w-xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        <div className={`transition-all duration-200 ease-out ${transition}`}>
          {screen === 'loading' && (
            <p className="text-gray-400">One moment — checking your invitation…</p>
          )}

          {screen === 'type' && (
            <>
              <h1 className="text-2xl sm:text-3xl font-bold text-black mb-2">What partner type are you?</h1>
              <p className="text-gray-400 mb-8">This decides what Doorly asks you for next.</p>
              <div className="space-y-3">
                {TYPE_OPTIONS.map((o) => (
                  <Choice
                    key={o.value} label={o.label} sublabel={o.sublabel}
                    onClick={() => { setAnswers({ partner_type: o.value }); goTo('role'); }}
                  />
                ))}
              </div>
            </>
          )}

          {screen === 'role' && (
            <>
              <h1 className="text-2xl sm:text-3xl font-bold text-black mb-2">{roleQ.heading}</h1>
              <p className="text-gray-400 mb-8">
                Setting your firm up is a different registration from joining one, so we ask first.
              </p>
              <div className="space-y-3">
                {roleQ.options.map((o) => (
                  <Choice
                    key={o.value} label={o.label} sublabel={o.sublabel}
                    onClick={() => finish(answers.partner_type, o.value)}
                  />
                ))}
              </div>
              {/* ⚠️ NO BACK BUTTON FOR AN INVITED PARTNER. Going back would offer
                  them a question their invite already answered, and a different
                  answer there would contradict the row staff recorded. */}
              {!inviteId && (
                <button
                  type="button"
                  onClick={() => goTo('type', 'back')}
                  className="mt-10 flex items-center gap-1.5 text-sm text-gray-400 hover:text-black transition-colors"
                >
                  ← Back
                </button>
              )}
            </>
          )}
        </div>

        <p className="mt-12 text-sm text-gray-400">
          Already have a DoorlyPro account?{' '}
          <Link to="/doorlypro/login" className="font-semibold" style={{ color: '#3a6ad9' }}>Sign in</Link>
        </p>
      </main>
    </div>
  );
}

export default DoorlyProStart;
